import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { demofile } from '../utils/paths';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * 视频：与仓库根目录 `轮播demo/` 同步到 `public/demofile/轮播demo/`
 */
const CAROUSEL_DEMO_FILENAMES = [
  'tv2a_fire_burning.mp4',
  'tv2a_motor_racing.mp4',
  'tv2a_skateboarding.mp4',
  'tcv2a_magic_thunder_strike.mp4',
] as const;

/**
 * 文生音频示例：与仓库根目录 `轮播demo_add/` 同步到 `public/demofile/轮播demo_add/`
 * 提示词与 `各子目录/提示词.txt` 一致，必须在卡片上展示。
 */
const CAROUSEL_ADD_AUDIO = [
  {
    dir: 'TTA1',
    file: 'A_bird_sings_melodically_in_a__inference_2 (2).flac',
    prompt: '森林里，一只鸟儿在婉转地歌唱',
    promptEn: 'A bird sings melodically in a forest',
  },
  {
    dir: 'TTA2',
    file: '篝火.flac',
    prompt: '轻柔的篝火噼啪声，柔和的木柴燃烧爆裂声，温暖纯净的环境音，清晰自然的音频',
    promptEn: 'Gentle campfire crackling, soft wood burning and popping, warm and pure ambient sound, clear and natural audio',
  },
] as const;

const CAROUSEL_GRADIENTS = [
  'from-purple-400 to-pink-300',
  'from-indigo-400 to-blue-500',
  'from-emerald-400 to-teal-300',
  'from-blue-400 to-cyan-300',
] as const;

type CarouselCard =
  | { id: number; kind: 'video'; src: string; poster: string; color: string; prompt?: string; promptEn?: string }
  | { id: number; kind: 'audio'; src: string; color: string; prompt: string; promptEn: string };

/** 仅加载当前与相邻卡片视频，减少首屏并行下载 */
function videoLoadDistance(index: number, active: number, len: number): number {
  const d = Math.abs(index - active);
  return Math.min(d, len - d);
}

function buildCards(): CarouselCard[] {
  const videos: CarouselCard[] = CAROUSEL_DEMO_FILENAMES.map((name, i) => {
    const card: CarouselCard = {
      id: i + 1,
      kind: 'video',
      src: demofile(`轮播demo/${encodeURIComponent(name)}`),
      poster: demofile(`轮播demo/${encodeURIComponent(name.replace(/\.mp4$/i, '.poster.jpg'))}`),
      color: CAROUSEL_GRADIENTS[i % CAROUSEL_GRADIENTS.length],
    };
    if (name === 'tcv2a_magic_thunder_strike.mp4') {
      card.prompt = 'thunder strike';
      card.promptEn = 'thunder strike';
    }
    return card;
  });

  const audios: CarouselCard[] = CAROUSEL_ADD_AUDIO.map((item, i) => ({
    id: videos.length + i + 1,
    kind: 'audio',
    src: demofile(`轮播demo_add/${item.dir}/${encodeURIComponent(item.file)}`),
    prompt: item.prompt,
    promptEn: item.promptEn,
    color: CAROUSEL_GRADIENTS[(videos.length + i) % CAROUSEL_GRADIENTS.length],
  }));

  return [...videos, ...audios];
}

function AudioWaveform({ isPlaying }: { isPlaying: boolean }) {
  const bars = Array.from({ length: 40 }, (_, i) => i);

  return (
    <div className="absolute inset-0 flex items-center justify-center px-8">
      <div className="flex items-end justify-center gap-1 w-full h-32">
        {bars.map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-white/80 rounded-t transition-all duration-300"
            style={{
              height: isPlaying ? `${20 + Math.random() * 80}%` : `${10 + Math.random() * 20}%`,
              animation: isPlaying
                ? `waveform ${0.5 + Math.random() * 0.5}s ease-in-out infinite alternate`
                : 'none',
              animationDelay: `${i * 0.05}s`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes waveform {
          0% { transform: scaleY(0.3); }
          100% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}

export default function ThreeDCarousel() {
  const { lang } = useLanguage();
  const CARDS = buildCards();

  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const expandedVideoRef = useRef<HTMLVideoElement | null>(null);
  /** 点击视频卡后待播放的索引（等 DOM 挂上 src 再 play，避免远端卡片此前未加载 src） */
  const [pendingVideoPlayIndex, setPendingVideoPlayIndex] = useState<number | null>(null);

  useEffect(() => {
    if (isPlaying) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % CARDS.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaying, CARDS.length]);

  useLayoutEffect(() => {
    if (pendingVideoPlayIndex === null || pendingVideoPlayIndex !== activeIndex) return;
    const idx = pendingVideoPlayIndex;
    const run = (frame: number) => {
      const v = videoRefs.current[idx];
      if (!v) {
        if (frame < 20) requestAnimationFrame(() => run(frame + 1));
        else setPendingVideoPlayIndex(null);
        return;
      }
      if (!v.currentSrc && !v.src && frame < 20) {
        requestAnimationFrame(() => run(frame + 1));
        return;
      }
      if (!v.src && !v.currentSrc) {
        setPendingVideoPlayIndex(null);
        return;
      }
      v.muted = isMuted;
      v.loop = false;
      v.currentTime = 0;
      v.play()
        .then(() => {
          setIsPlaying(true);
          setPendingVideoPlayIndex(null);
        })
        .catch((err) => {
          console.warn('Video play failed:', err);
          setPendingVideoPlayIndex(null);
        });
    };
    run(0);
  }, [activeIndex, pendingVideoPlayIndex, isMuted]);

  const stopAllMedia = () => {
    videoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });
    audioRefs.current.forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
  };

  const handleCardClick = (index: number) => {
    stopAllMedia();
    setActiveIndex(index);

    const card = CARDS[index];
    if (card.kind === 'video') {
      setPendingVideoPlayIndex(index);
    } else {
      setPendingVideoPlayIndex(null);
      const audio = audioRefs.current[index];
      if (!audio) return;
      audio.muted = isMuted;
      audio.loop = false;
      audio.currentTime = 0;
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Audio play failed:', err);
        });
    }
  };

  const handleMediaEnded = () => {
    setIsPlaying(false);
  };

  const toggleMute = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    const card = CARDS[index];
    if (card.kind === 'video') {
      const video = videoRefs.current[index];
      if (video) video.muted = nextMuted;
    } else {
      const audio = audioRefs.current[index];
      if (audio) audio.muted = nextMuted;
    }
  };

  const handleFullscreen = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (CARDS[index].kind !== 'video') return;
    setExpandedIndex(index);
  };

  const getCardStyle = (index: number) => {
    const length = CARDS.length;

    let diff = (index - activeIndex + length) % length;
    if (diff > length / 2) diff -= length;

    const isActive = diff === 0;
    const isNext = diff === 1;
    const isPrev = diff === -1 || diff === length - 1;

    let transform = '';
    let zIndex = 0;
    let opacity = 0;

    if (isActive) {
      transform = 'translateX(0) translateZ(0) rotateY(0deg) scale(1)';
      zIndex = 20;
      opacity = 1;
    } else if (isPrev) {
      transform = 'translateX(-70%) translateZ(-100px) rotateY(35deg) scale(0.85)';
      zIndex = 10;
      opacity = 0.8;
    } else if (isNext) {
      transform = 'translateX(70%) translateZ(-100px) rotateY(-35deg) scale(0.85)';
      zIndex = 10;
      opacity = 0.8;
    } else {
      transform = 'translateZ(-200px) scale(0.5)';
      zIndex = 0;
      opacity = 0;
    }

    return {
      transform,
      zIndex,
      opacity,
      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  };

  if (CARDS.length === 0) {
    return null;
  }

  const len = CARDS.length;

  return (
    <>
      <div
        className="relative w-full h-[400px] md:h-[500px] flex justify-center items-center"
        style={{ perspective: '1000px' }}
      >
        {CARDS.map((card, index) => {
          const style = getCardStyle(index);
          const vDist = card.kind === 'video' ? videoLoadDistance(index, activeIndex, len) : 99;
          const loadVideoSrc =
            card.kind === 'video' &&
            (
              (isPlaying && index === activeIndex) ||
              pendingVideoPlayIndex === index ||
              expandedIndex === index ||
              vDist <= 1
            );

          return (
            <div
              key={card.id}
              className="absolute w-[280px] md:w-[340px] h-[380px] md:h-[460px] rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-100"
              style={style}
              onClick={() => handleCardClick(index)}
            >
              <div className="relative w-full h-full group">
                {card.kind === 'video' ? (
                  <>
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el;
                      }}
                      src={loadVideoSrc ? card.src : undefined}
                      poster={card.poster}
                      className="w-full h-full object-cover"
                      muted={isMuted}
                      loop={false}
                      playsInline
                      preload={loadVideoSrc ? 'metadata' : 'none'}
                      fetchPriority={loadVideoSrc && vDist === 0 ? 'high' : 'low'}
                      onEnded={handleMediaEnded}
                    />
                    <div className={`absolute inset-0 opacity-20 bg-gradient-to-br ${card.color}`}></div>
                    {card.prompt && (
                      <div className="absolute bottom-0 left-0 right-0 z-[5] px-3 pb-3 pt-10 bg-gradient-to-t from-black/75 via-black/40 to-transparent">
                        <p className="text-white text-xs sm:text-sm leading-snug drop-shadow-md text-left">
                          <span className="text-white/80 mr-1">{lang === 'zh' ? '提示词' : 'Prompt'}</span>
                          {card.prompt}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-90`}></div>
                    <AudioWaveform isPlaying={isPlaying && index === activeIndex} />
                    <audio
                      ref={(el) => {
                        audioRefs.current[index] = el;
                      }}
                      src={card.src}
                      muted={isMuted}
                      loop={false}
                      autoPlay={false}
                      onEnded={handleMediaEnded}
                    />
                    {/* 提示词（必显） */}
                    <div className="absolute bottom-0 left-0 right-0 z-[5] px-3 pb-3 pt-10 bg-gradient-to-t from-black/75 via-black/40 to-transparent">
                      <p className="text-white text-xs sm:text-sm leading-snug drop-shadow-md line-clamp-6 text-left">
                        <span className="text-white/80 mr-1">{lang === 'zh' ? '提示词' : 'Prompt'}</span>
                        {lang === 'en' ? card.promptEn : card.prompt}
                      </p>
                    </div>
                  </>
                )}

                {index === activeIndex && (
                  <div className="absolute bottom-4 right-4 z-[15] flex items-center gap-2">
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full bg-white text-gray-800 shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors"
                      onClick={(e) => toggleMute(e, index)}
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    {card.kind === 'video' && (
                      <button
                        type="button"
                        className="w-10 h-10 rounded-full bg-white text-gray-800 shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors"
                        onClick={(e) => handleFullscreen(e, index)}
                      >
                        <Maximize2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {expandedIndex !== null &&
        CARDS[expandedIndex] &&
        CARDS[expandedIndex].kind === 'video' && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => {
              if (expandedVideoRef.current) {
                expandedVideoRef.current.pause();
              }
              setExpandedIndex(null);
            }}
          >
            <div
              className="relative bg-black rounded-xl overflow-hidden shadow-2xl"
              style={{ height: '50vh', width: 'auto', aspectRatio: '16 / 9' }}
              onClick={(e) => e.stopPropagation()}
            >
              <video
                ref={expandedVideoRef}
                src={CARDS[expandedIndex].src}
                poster={CARDS[expandedIndex].kind === 'video' ? CARDS[expandedIndex].poster : undefined}
                className="w-full h-full object-contain"
                muted={isMuted}
                autoPlay
                controls
                preload="auto"
              />
              <button
                type="button"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 text-gray-800 flex items-center justify-center hover:bg-white transition-colors text-sm font-bold"
                onClick={() => {
                  if (expandedVideoRef.current) {
                    expandedVideoRef.current.pause();
                  }
                  setExpandedIndex(null);
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
    </>
  );
}
